import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import ScanStateBadge from './ScanStateBadge';
import { formatBytes, formatCount, formatDate, formatNullableBool, formatNullableNumber } from './format';
import { getSiteInventoryDetail, type SiteInventoryDetailDto } from '@/api/sharepointInventoryApi';
import { usePageTitle } from '@/hooks/usePageTitle';

const libraryStatusVariant = (status: string) => {
  switch (status) {
    case 'Complete': return 'success';
    case 'Failed': return 'danger';
    case 'Running': return 'info';
    default: return 'secondary';
  }
};

const SiteDetailPage: React.FC = () => {
  const { siteCollectionId } = useParams<{ siteCollectionId: string }>();
  const [detail, setDetail] = useState<SiteInventoryDetailDto | null>(null);
  const [selectedWeb, setSelectedWeb] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteCollectionId) return;
    setLoading(true);
    getSiteInventoryDetail(Number(siteCollectionId))
      .then((d) => {
        setDetail(d);
        setError(null);
      })
      .catch(() => setError('Failed to load site inventory.'))
      .finally(() => setLoading(false));
  }, [siteCollectionId]);

  const webs = useMemo(
    () => Array.from(new Set((detail?.libraries ?? []).map((l) => l.webRelativeUrl))).sort(),
    [detail]
  );

  usePageTitle(detail?.site.title || detail?.site.url, 'Site Details');

  const visibleLibraries = useMemo(
    () => (detail?.libraries ?? []).filter((l) => !selectedWeb || l.webRelativeUrl === selectedWeb),
    [detail, selectedWeb]
  );

  if (loading) return <Container className="py-3"><Spinner animation="border" /></Container>;
  if (error) return <Container className="py-3"><Alert variant="danger">{error}</Alert></Container>;
  if (!detail) return <Container className="py-3"><Alert variant="warning">Site not found.</Alert></Container>;

  const { site, libraries } = detail;
  const pendingLibraries = libraries.filter((l) => l.status === 'Pending').length;
  return (
    <Container fluid className="py-3">
      <div className="mb-3">
        <Link to="/admin/sharepoint/inventory" className="small">&larr; Back to inventory</Link>
        <h3 className="mb-1 mt-2">{site.title || site.url}</h3>
        <div className="text-muted small">{site.url}</div>
      </div>

      <Row className="g-3 mb-3">
        <Col md={3}><Card body><div className="text-muted small">Scan state</div><div className="fs-5"><ScanStateBadge state={site.scanState} /></div></Card></Col>
        <Col md={3}><Card body><div className="text-muted small">Inventoried files</div><div className="fs-4">{formatCount(site.fileCount)}</div></Card></Col>
        <Col md={3}><Card body><div className="text-muted small">Inventoried storage</div><div className="fs-4">{formatBytes(site.totalBytes)}</div></Card></Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Last completed scan</div>
            <div className="fs-5">{formatDate(site.lastCompletedCrawlDt)}</div>
            <div className="text-muted small">Last attempt: {formatDate(site.lastAttemptCrawlDt)} {site.lastAttemptStatus ? `(${site.lastAttemptStatus})` : ''}</div>
          </Card>
        </Col>
      </Row>

      <div className="mb-3">
        <Link
          className="btn btn-outline-primary btn-sm"
          to={`/admin/sharepoint/inventory/files?siteCollectionId=${site.siteCollectionId}`}
        >
          Browse this site&apos;s files
        </Link>
      </div>

      {pendingLibraries > 0 && (
        <Alert variant="warning">
          {pendingLibraries} librar{pendingLibraries === 1 ? 'y was' : 'ies were'} not scanned in the latest completed
          crawl. File counts for this site are incomplete.
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>Libraries (from the latest completed scan)</span>
          {webs.length > 1 && (
            <Form.Select
              size="sm"
              style={{ maxWidth: 360 }}
              value={selectedWeb}
              onChange={(e) => setSelectedWeb(e.target.value)}
            >
              <option value="">All webs ({webs.length})</option>
              {webs.map((w) => (
                <option key={w} value={w}>{w || '(root web)'}</option>
              ))}
            </Form.Select>
          )}
        </Card.Header>
        <Card.Body>
          <Table hover size="sm" responsive>
            <thead>
              <tr>
                <th>Web</th>
                <th>Library</th>
                <th className="text-end">Items reported</th>
                <th className="text-end">Files observed</th>
                <th>Status</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {visibleLibraries.map((l) => (
                <tr key={l.listId}>
                  <td className="text-muted small">{l.webRelativeUrl || '(root)'}</td>
                  <td>
                    <div>{l.title}</div>
                    <div className="text-muted small">{l.rootFolderUrl}</div>
                  </td>
                  <td className="text-end">{formatNullableNumber(l.itemCount)}</td>
                  <td className="text-end">{formatCount(l.filesObserved)}</td>
                  <td><Badge bg={libraryStatusVariant(l.status)}>{l.status}</Badge></td>
                  <td className="text-danger small">{l.errorMessage}</td>
                </tr>
              ))}
              {visibleLibraries.length === 0 && (
                <tr><td colSpan={6} className="text-muted">No completed scan for this site yet.</td></tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Versioning Review</Card.Header>
        <Card.Body>
          <div className="text-muted small mb-3">
            Library versioning settings as captured by the crawl. These settings describe configuration only &mdash;
            they do not indicate how many versions exist or how much storage version history consumes.
          </div>
          <Table hover size="sm" responsive>
            <thead>
              <tr>
                <th>Web</th>
                <th>Library</th>
                <th>Versioning enabled</th>
                <th>Minor versions</th>
                <th className="text-end">Major version limit</th>
                <th className="text-end">Major w/ minor limit</th>
              </tr>
            </thead>
            <tbody>
              {visibleLibraries.map((l) => (
                <tr key={l.listId}>
                  <td className="text-muted small">{l.webRelativeUrl || '(root)'}</td>
                  <td>{l.title}</td>
                  <td>{formatNullableBool(l.enableVersioning)}</td>
                  <td>{formatNullableBool(l.enableMinorVersions)}</td>
                  <td className="text-end">{formatNullableNumber(l.majorVersionLimit)}</td>
                  <td className="text-end">{formatNullableNumber(l.majorWithMinorVersionsLimit)}</td>
                </tr>
              ))}
              {visibleLibraries.length === 0 && (
                <tr><td colSpan={6} className="text-muted">No completed scan for this site yet.</td></tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SiteDetailPage;
